import React from "react";
import { groupBy } from "lodash";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { ButtonGrid } from "@sonder/ui/source/styled/fragments/ButtonGrid";
import {
  GetTaskLocationsLocation,
  useGetTaskLocationsQuery,
} from "core/queries/GetTaskLocations";
import { ButtonGridMultiContentLoader } from "tasks/components/ButtonGrid.loader";
import { TaskContextSubjectTypeEnum, TypeEnum } from "typings/globalGQLTypes";

interface TaskLocationsProps {
  subjectId: number;
  subjectType: TaskContextSubjectTypeEnum;
  taskType: TypeEnum;
  value: GetTaskLocationsLocation | null;
  onChange?: (location: GetTaskLocationsLocation) => void;
}

export const TaskLocations = ({
  onChange,
  subjectId,
  subjectType,
  taskType,
  value,
}: TaskLocationsProps) => {
  const { loading, data, error } = useGetTaskLocationsQuery({
    skip: !subjectId || !subjectType,
    variables: {
      filters: {
        subjectId,
        subjectType,
        taskContextTaskTypes: [taskType],
      },
    },
  });

  if (!subjectId || !subjectType) {
    return null;
  }

  const locations = data?.taskLocations;
  const locationsGroupedByPlace = groupBy(
    locations,
    location => location.place,
  );
  const shouldShowGroups = Object.keys(locationsGroupedByPlace).length > 1;

  const renderButton = (location: GetTaskLocationsLocation) => (
    <Button
      key={location.id}
      selected={value?.id === location.id}
      onClick={() => {
        if (onChange) {
          onChange(location);
        }
      }}
    >
      {location.name}
    </Button>
  );

  const renderMultiGrid = () => (
    <>
      {Object.keys(locationsGroupedByPlace).map(place => {
        const groupedLocations = locationsGroupedByPlace[place];
        return (
          <React.Fragment key={place}>
            <h4>{place}</h4>
            <ButtonGrid>{groupedLocations.map(renderButton)}</ButtonGrid>
          </React.Fragment>
        );
      })}
    </>
  );

  const renderSingleGrid = () => (
    <ButtonGrid>{(locations || []).map(renderButton)}</ButtonGrid>
  );

  return (
    <>
      {loading && <ButtonGridMultiContentLoader />}
      {error && error.message && `There was an error: ${error.message}.`}
      {locations && (shouldShowGroups ? renderMultiGrid() : renderSingleGrid())}
    </>
  );
};
